import React from 'react'
import type { VisibilityRule, VisibilityCondition, Variable } from '../../types'
import { Eye, Plus, X } from 'lucide-react'

interface VisibilityEditorProps {
  visibility?: VisibilityRule
  variables: Variable[]
  onChange: (visibility: VisibilityRule | undefined) => void
}

const OPERATORS: { value: VisibilityCondition['operator']; label: string }[] = [
  { value: '==', label: '=' },
  { value: '!=', label: '≠' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '≥' },
  { value: '<=', label: '≤' },
]

function PropertyLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
      {children}
    </label>
  )
}

function getVariableValueOptions(variable: Variable | undefined): { label: string; value: string }[] | null {
  if (!variable) return null
  if (variable.type === 'toggle') {
    return [
      { label: 'true', value: 'true' },
      { label: 'false', value: 'false' },
    ]
  }
  return null
}

function getDefaultValueForVariable(variable: Variable | undefined): number | string | boolean {
  if (!variable) return ''
  if (variable.type === 'toggle') return 'true'
  if (variable.type === 'slider' || variable.type === 'formula') return 0
  return ''
}

export default function VisibilityEditor({ visibility, variables, onChange }: VisibilityEditorProps) {
  const isEnabled = !!visibility

  const handleToggle = () => {
    if (isEnabled) {
      onChange(undefined)
    } else {
      const firstVar = availableVars[0]
      onChange({
        conditions: [{ variable: firstVar?.name || '', operator: '==', value: getDefaultValueForVariable(firstVar) }],
        logic: 'and',
      })
    }
  }

  const update = (updates: Partial<VisibilityRule>) => {
    if (!visibility) return
    onChange({ ...visibility, ...updates })
  }

  const updateCondition = (index: number, updates: Partial<VisibilityCondition>) => {
    if (!visibility) return
    const newConditions = visibility.conditions.map((c, i) =>
      i === index ? { ...c, ...updates } : c
    )
    onChange({ ...visibility, conditions: newConditions })
  }

  const addCondition = () => {
    if (!visibility) return
    const firstVar = availableVars[0]
    onChange({
      ...visibility,
      conditions: [...visibility.conditions, { variable: firstVar?.name || '', operator: '==', value: getDefaultValueForVariable(firstVar) }],
    })
  }

  const removeCondition = (index: number) => {
    if (!visibility) return
    const newConditions = visibility.conditions.filter((_, i) => i !== index)
    if (newConditions.length === 0) {
      onChange(undefined)
    } else {
      onChange({ ...visibility, conditions: newConditions })
    }
  }

  // Filter to only interactive variables (not formulas — formulas can be referenced though)
  const availableVars = variables.filter((v) => v.name)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Eye className="h-3.5 w-3.5 text-slate-400" />
          <PropertyLabel>Visibility</PropertyLabel>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={isEnabled}
            onChange={handleToggle}
          />
          <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#5048e5]"></div>
        </label>
      </div>

      {isEnabled && (
        <>
          {visibility.conditions.length > 1 && (
            <div className="flex items-center gap-2">
              <PropertyLabel>Logic</PropertyLabel>
              <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
                {(['and', 'or'] as const).map((logic) => (
                  <button
                    key={logic}
                    onClick={() => update({ logic })}
                    className={`px-3 py-1 text-[10px] font-bold uppercase rounded-md transition-all ${
                      visibility.logic === logic
                        ? 'bg-white text-[#5048e5] shadow-sm'
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {logic}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {visibility.conditions.map((condition, index) => {
              const selectedVar = availableVars.find((v) => v.name === condition.variable)
              const valueOptions = getVariableValueOptions(selectedVar)

              return (
                <div key={index} className="flex items-center gap-1.5 bg-slate-50 rounded-lg p-2 border border-slate-100">
                  {/* Variable */}
                  <select
                    value={condition.variable}
                    onChange={(e) => {
                      const newVar = availableVars.find((v) => v.name === e.target.value)
                      updateCondition(index, { variable: e.target.value, value: getDefaultValueForVariable(newVar) })
                    }}
                    className="flex-1 min-w-0 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono focus:ring-1 focus:ring-[#5048e5] outline-none"
                  >
                    <option value="">--</option>
                    {availableVars.map((v) => (
                      <option key={v.name} value={v.name}>@{v.name}</option>
                    ))}
                  </select>

                  {/* Operator */}
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(index, { operator: e.target.value as VisibilityCondition['operator'] })}
                    className="w-12 bg-white border border-slate-200 rounded px-1 py-1.5 text-xs text-center focus:ring-1 focus:ring-[#5048e5] outline-none"
                  >
                    {OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>

                  {/* Value */}
                  {valueOptions ? (
                    <select
                      value={String(condition.value)}
                      onChange={(e) => updateCondition(index, { value: e.target.value })}
                      className="flex-1 min-w-0 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#5048e5] outline-none"
                    >
                      {valueOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={String(condition.value)}
                      onChange={(e) => {
                        const raw = e.target.value
                        const num = Number(raw)
                        updateCondition(index, { value: raw === '' ? '' : isNaN(num) ? raw : num })
                      }}
                      placeholder="value"
                      className="flex-1 min-w-0 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#5048e5] outline-none"
                    />
                  )}

                  {/* Remove */}
                  <button
                    onClick={() => removeCondition(index)}
                    className="p-1 text-slate-300 hover:text-red-400 transition-colors shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            })}
          </div>

          <button
            onClick={addCondition}
            className="flex items-center gap-1.5 text-xs text-[#5048e5] font-medium hover:text-[#4038c5] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add condition
          </button>
        </>
      )}
    </div>
  )
}
